import {
  EnvelopeOpenIcon,
  GlobeEuropeAfricaIcon,
} from '@heroicons/react/24/outline';
import type { Route } from 'next';
import Link from 'next/link';

import ScrollButton from '../ScrollButton';
import { Button } from '../tailwind-ui';

import { FooterNewsletter } from './newsletter/FooterNewsletter';

interface FooterProps {
  isNewsletterVisible?: boolean;
}

interface FooterItem {
  label: string;
  href: Route;
  blank?: boolean;
}

interface FooterState {
  tools: FooterItem[];
  resources: FooterItem[];
  company: FooterItem[];
}

const footerItems: FooterState = {
  tools: [
    { label: 'NMRium', href: '/nmrium' },
    { label: 'NMRium with demo dataset', href: '/nmrium/demo' },
    { label: 'Predict', href: '/predict' as Route },
    { label: 'Predict from SMILES', href: '/prediction' },
    { label: 'Simulation', href: '/simulation' },
  ],
  resources: [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Features', href: '/features' },
    { label: 'Documentation', href: 'https://docs.nmrium.org/' },
    { label: 'Teaching', href: '/teaching' },
    { label: 'Tutorials', href: '/tutorials' },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'Contact Us', href: '/contact' },
    // privacy-policy is a static (and correct) page
    {
      label: 'Privacy Policy',
      href: '/privacy-policy.html' as Route,
      blank: true,
    },
  ],
};

export default function Footer(props: FooterProps) {
  const { isNewsletterVisible } = props;

  return (
    <>
      {isNewsletterVisible && <FooterNewsletter />}
      <footer className="bg-white relative">
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-8 sm:pt-24 lg:px-8 lg:pt-32">
          <div className="text-right bottom-2/12 absolute right-1/12">
            <ScrollButton />
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8">
            <div className="flex flex-col gap-8">
              <a className="flex w-fit" href="/public">
                <img
                  width={150}
                  src="/brand/zakodium-brand.svg"
                  alt="Zakodium brand"
                />
              </a>
              <div className="flex flex-row gap-5">
                <Link
                  href="https://www.linkedin.com/company/zakodium"
                  target="_blank"
                >
                  <img
                    alt="linkedin"
                    src="/social/linkedin.png"
                    className="size-6"
                  />
                </Link>

                <Link
                  href="https://bsky.app/profile/did:plc:66dszou53nikjcnjh22dz2b4"
                  target="_blank"
                >
                  <img
                    alt="bluesky"
                    src="/social/bluesky.svg"
                    className="size-6"
                  />
                </Link>

                <Link href="https://www.zakodium.com/" target="_blank">
                  <GlobeEuropeAfricaIcon className="size-6" />
                </Link>
              </div>
              <div className="flex flex-col xl:flex-row gap-10 justify-between">
                <div className="flex flex-col gap-1 text-sm/6 text-neutral-600">
                  <p>Zakodium Sàrl</p>
                  <p>Route d&#39;Echandens 6b</p>
                  <p>1027 Lonay</p>
                  <p>Switzerland</p>
                </div>
                <div>
                  <Button
                    as="a"
                    href="/contact"
                    className="flex flex-row gap-2 w-fit px-5 items-center"
                  >
                    <EnvelopeOpenIcon className="size-5" />
                    <span>Contact us</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-16 lg:mt-0 sm:col-span-2 space-y-10 gap-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4">
              <div className="xl:block hidden" />
              <div className="w-40">
                <h3 className="text-sm/6 font-semibold text-neutral-900">
                  Tools
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {footerItems.tools.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm/6 text-neutral-600 hover:text-neutral-900"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-40">
                <h3 className="text-sm/6 font-semibold text-neutral-900">
                  Resources
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {footerItems.resources.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm/6 text-neutral-600 hover:text-neutral-900"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-40">
                <h3 className="text-sm/6 font-semibold text-neutral-900">
                  Company
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {footerItems.company.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        target={item.blank ? '_blank' : undefined}
                        rel={item.blank ? 'noreferrer' : undefined}
                        className="text-sm/6 text-neutral-600 hover:text-neutral-900"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-16 border-t border-neutral-900/10 pt-8 sm:mt-20 lg:mt-24">
            <p className="text-sm/6 text-neutral-600">
              &copy; Zakodium Sàrl. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
